import { forwardRef, useMemo, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { getIDFromUrl, Url } from '@rossum/api-client';
import PaperSection from '@rossum/components/paper-section/PaperSection';
import { IconSearch } from '@rossum/ui/icons/tabler';
import {
  Autocomplete,
  CircularProgress,
  ListItem,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
} from '@rossum/ui/material';
import { useEngine } from '../hooks/useEngine';
import { useEngines } from '../hooks/useEngines';

export type EngineQueueSettingsValue = {
  engine: Url | null;
};

type EngineQueueSettingsProps = {
  queueEngine: Url;
  value: EngineQueueSettingsValue;
  onChange: (value: EngineQueueSettingsValue) => void;
  error?: FieldError;
  disabled?: boolean;
};

export const EngineQueueSettings = forwardRef(
  (
    { queueEngine, value, onChange, error, disabled }: EngineQueueSettingsProps,
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const queueEngineId = getIDFromUrl(queueEngine);

    const { data: engine, isFetching: engineIsFetching } =
      useEngine(queueEngineId);

    const { data: engines, isFetching: enginesAreFetching } = useEngines(
      undefined,
      open
    );

    const intl = useIntl();

    const labelProps = {
      size: 'small' as const,
      label: intl.formatMessage({
        id: 'features.engines.queueSettings.engine.label',
      }),
    };

    const selectedValue = useMemo(
      () =>
        engine?.url === value.engine
          ? engine
          : engines?.results.find(option => option.url === value.engine),
      [engine, engines?.results, value.engine]
    );

    return (
      <PaperSection
        dataCy="engine-queue-settings"
        title={intl.formatMessage({
          id: 'features.engines.queueSettings.title',
        })}
      >
        <Stack
          spacing={2}
          sx={{
            width: '400px',
            maxWidth: '100%',
            justifyContent: 'flex-start',
          }}
        >
          {engineIsFetching ? (
            <TextField
              {...labelProps}
              disabled
              InputProps={{
                startAdornment: <Skeleton width="250px" />,
              }}
            />
          ) : (
            <Autocomplete
              ref={ref}
              disabled={disabled}
              loading={enginesAreFetching}
              loadingText={intl.formatMessage({
                id: 'features.engines.queueSettings.loading',
              })}
              id="engineGroup.engine"
              clearIcon={false}
              fullWidth
              disableClearable
              value={selectedValue}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              isOptionEqualToValue={(option, currentValue) =>
                option.url === currentValue?.url
              }
              onChange={(_, option) =>
                onChange({
                  engine: option?.url ?? null,
                })
              }
              getOptionLabel={engineOption => engineOption.name}
              renderOption={(props, engineOption) => (
                <ListItem {...props} key={engineOption.id}>
                  {engineOption.name}
                </ListItem>
              )}
              renderInput={params => (
                <TextField
                  helperText={error ? error.message : ''}
                  error={!!error}
                  {...params}
                  {...labelProps}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: value.engine ? null : (
                      <SvgIcon fontSize="medium">
                        <IconSearch />
                      </SvgIcon>
                    ),
                    endAdornment: (
                      <>
                        {enginesAreFetching ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              options={engines?.results ?? []}
            />
          )}
        </Stack>
      </PaperSection>
    );
  }
);
